/**
 * Checklist Service — all I/O for the DIY Comparison Checklist feature.
 * Called only from hooks; never imported directly by UI components.
 */

const API = process.env.REACT_APP_BACKEND_URL;
if (!API) throw new Error('REACT_APP_BACKEND_URL is required');

const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  NOT_FOUND: 'NOT_FOUND',
};

/**
 * Fetch the list of available checklist template summaries.
 * @returns {Promise<{ success: boolean, data?: Array, error?: { code: string, message: string } }>}
 */
export const fetchTemplates = async () => {
  try {
    const res = await fetch(`${API}/api/checklist/templates`);
    if (!res.ok) {
      return { success: false, error: { code: ERROR_CODES.NETWORK_ERROR, message: `HTTP ${res.status}` } };
    }
    return { success: true, data: await res.json() };
  } catch (e) {
    return { success: false, error: { code: ERROR_CODES.NETWORK_ERROR, message: e.message } };
  }
};

/**
 * Fetch a single checklist template by its ID.
 * @param {string} id  Template identifier
 * @returns {Promise<{ success: boolean, data?: Object, error?: { code: string, message: string } }>}
 */
export const fetchTemplate = async (id) => {
  try {
    const res = await fetch(`${API}/api/checklist/templates/${encodeURIComponent(id)}`);
    if (res.status === 404) {
      return { success: false, error: { code: ERROR_CODES.NOT_FOUND, message: `Template '${id}' not found` } };
    }
    if (!res.ok) {
      return { success: false, error: { code: ERROR_CODES.NETWORK_ERROR, message: `HTTP ${res.status}` } };
    }
    return { success: true, data: await res.json() };
  } catch (e) {
    return { success: false, error: { code: ERROR_CODES.NETWORK_ERROR, message: e.message } };
  }
};

/**
 * Trigger a CSV file download in the browser (side-effect).
 * @param {string} csvContent  Serialised CSV string
 * @param {string} filename    Suggested download filename
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
